<script setup>
import { useDevice } from "#imports";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ref, computed } from "vue";
import ChargemapLogo from "../../images/ChargemapLogo.vue";
import MenuButton from "./navigation/MenuButton.vue";
const { isIos } = useDevice();
const { t } = useI18n();
const route = useRoute();
defineEmits(["signin", "signup"]);
const props = defineProps({
  language: { type: String, required: true },
  country: { type: String, required: true },
  s3Url: { type: String, required: true },
  userId: { type: Number, required: true },
  defaultAvatarPath: { type: String, required: true },
  userAvatar: { type: String, required: false },
  cdnUrl: { type: String, required: false },
  chargemapUrl: { type: String, required: true }
});
const isMenuOpen = ref(false);
const chargemapLogoSquarePath = computed(
  () => props.cdnUrl && `${props.cdnUrl}/images/logo-square-light.svg` || ""
);
const menuButtons = computed(() => {
  return [
    {
      class: "gtm-NavBarPrincipale",
      label: t("word.map"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/map`,
      active: isCurrentRoute(`/${props.language}-${props.country}/map`)
    },
    {
      class: "gtm-NavBarPrincipale",
      label: t("word.mobile"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/mobile`,
      active: isCurrentRoute(`/${props.language}-${props.country}/mobile`)
    },
    {
      class: "gtm-NavBarPrincipale md:hidden",
      label: t("word.planner"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/mobile/planner`,
      active: isCurrentRoute(
        `${props.chargemapUrl}/${props.language}-${props.country}/mobile/planner`
      )
    },
    {
      class: "gtm-NavBarPrincipale",
      label: t("chargemap_pass"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/pass`,
      active: isCurrentRoute(`/${props.language}-${props.country}/pass`)
    },
    {
      class: "gtm-NavBarPrincipale",
      label: t("word.community"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/community`,
      active: isCurrentRoute(
        `${props.chargemapUrl}/${props.language}-${props.country}/community`
      )
    },
    {
      class: "gtm-NavBarPrincipale",
      label: t("word.companies"),
      href: businessUrl.value
    },
    {
      class: "gtm-NavBarPrincipale",
      label: t("menu.your_account"),
      href: `${props.chargemapUrl}/${props.language}-${props.country}/user/index`,
      account: true
    },
    {
      class: "gtm-NavBarPrincipale md:hidden",
      label: t("action.download_app"),
      href: storeUrl.value,
      active: true
    }
  ];
});
const businessUrl = computed(() => {
  switch (props.language) {
    case "de":
      return "https://chargemap-business.com/de";
    case "fr":
      return "https://chargemap-business.com";
    default:
      return "https://chargemap-business.com/en";
  }
});
const storeUrl = computed(
  () => isIos ? `https://itunes.apple.com/${props.country}/app/chargemap-bornes-de-recharge/id438176982` : `https://play.google.com/store/apps/details?id=com.chargemap_beta.android&hl=${props.language}`
);
const isCurrentRoute = (routeName) => route.path === routeName;
</script>

<template>
    <nav>
        <div
            class="flex items-center w-full md:w-auto h-14"
            :class="[userId && 'justify-between']"
        >
            <button
                id="burger"
                aria-label="Menu"
                class="normal md:hidden"
                type="button"
                :class="isMenuOpen ? 'active' : ''"
                @click="isMenuOpen = !isMenuOpen"
            >
                <span />
                <span />
                <span />
                <span />
            </button>
            <NuxtLink
                class="absolute min-[425px]:w-[190px] left-1/2 transform -translate-x-2/4 flex items-center justify-start md:relative"
                title="Chargemap"
                :to="`${props.chargemapUrl}/${language}-${country}`"
            >
                <ChargemapLogo
                    class="gtm-NavBarPrincipale h-10 w-40 hidden min-[425px]:block"
                />
                <NuxtImg
                    :src="chargemapLogoSquarePath"
                    alt="Chargemap"
                    class="gtm-NavBarPrincipale h-10 w-40 min-[425px]:hidden"
                />
            </NuxtLink>
        </div>

        <!--      MOBILE  -->
        <div
            v-if="isMenuOpen"
            class="flex md:hidden flex-col justify-start w-full bg-white transition-nav-bar duration-500 ease-in-out overflow-y-hidden z-10 pb-2 border-b border-gray-navbar max-h-96"
        >
            <div class="menu-buttons-container">
                <MenuButton
                    v-for="button in menuButtons"
                    :key="button.label"
                    v-bind="button"
                />
            </div>
        </div>

        <!--     DESKTOP  -->
        <div
            class="hidden md:flex flex-row items-center gap-2 w-auto relative bg-white border-0 max-h-full transition-nav-bar duration-500 ease-in-out overflow-hidden transition-none"
        >
            <div class="menu-buttons-container">
                <MenuButton
                    v-for="button in menuButtons"
                    :key="button.label"
                    v-bind="button"
                />
            </div>
        </div>
    </nav>
</template>

<style scoped>
nav {
  @apply w-full min-h-14 px-15 bg-white flex flex-col md:flex-row items-center md:justify-between z-10;
}
nav .menu-buttons-container {
  @apply flex flex-col md:flex-row items-start md:items-center justify-center gap-5 md:gap-3.5;
}
nav #burger {
  @apply relative transform rotate-0 ease-in-out duration-500 h-3.5 w-[22px];
}
nav #burger.active span:nth-child(1), nav #burger.active span:nth-child(4) {
  @apply left-1/2 w-0 top-1.5;
}
nav #burger.active span:nth-child(2) {
  @apply transform rotate-45;
}
nav #burger.active span:nth-child(3) {
  @apply transform -rotate-45;
}
nav #burger:hover > span {
  @apply bg-sky-500;
}
nav #burger span {
  @apply block absolute bg-zinc-800 opacity-100 left-0 transform rotate-0 ease-in-out duration-200 w-full h-0.5;
}
nav #burger span:nth-child(1) {
  @apply top-0;
}
nav #burger span:nth-child(2), nav #burger span:nth-child(3) {
  @apply top-1.5;
}
nav #burger span:nth-child(4) {
  @apply top-3;
}
</style>
